import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface ProfileData {
  full_name: string;
  phone: string;
  email: string;
}

export default function Profile() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [profileData, setProfileData] = useState<ProfileData>({
    full_name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadProfile();
  }, [user, navigate]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, phone')
        .eq('id', user?.id)
        .maybeSingle();

      // Profil yoksa oluştur
      if (!data && !error) {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user?.id,
            email: user?.email,
            full_name: user?.user_metadata?.full_name || '',
            phone: user?.user_metadata?.phone || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) {
          console.error('Profil oluşturma hatası:', insertError);
        }

        setProfileData({
          full_name: user?.user_metadata?.full_name || '',
          phone: user?.user_metadata?.phone || '',
          email: user?.email || ''
        });
      } else if (data) {
        setProfileData({
          full_name: data.full_name || '',
          phone: data.phone || '',
          email: user?.email || ''
        });
      }
    } catch (error) {
      console.error('Profil yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user?.id,
          email: user?.email,
          full_name: profileData.full_name,
          phone: profileData.phone,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setMessage('Profil başarıyla güncellendi! ✅');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Profil güncellenirken hata:', error);
      setMessage('Profil güncellenirken bir hata oluştu ❌');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#121C2E' }}>
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin"></i>
          <p className="mt-4 text-white/70">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-blue-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="bg-[#1a2942]/80 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <Link to="/" className="text-2xl font-bold text-white cursor-pointer">
                pad<span className="text-orange-500">baq</span>
              </Link>
            </div>
            <nav className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Panel</Link>
              <Link to="/boxes" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Koli Yönetimi</Link>
              <Link to="/profile" className="text-orange-500 font-medium px-4 py-2 rounded-lg bg-white/10 whitespace-nowrap cursor-pointer">Profil</Link>
              <button 
                onClick={handleLogout}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-circle-line mr-2"></i>
                Çıkış
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Profil Ayarları</h1>
          <p className="text-white/60">Hesap bilgilerinizi görüntüleyin ve düzenleyin</p>
        </div>

        {/* Profile Card */}
        <div className="bg-[#1a2942]/60 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-orange-500/20 to-blue-500/20 p-8 border-b border-white/10">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white text-4xl font-bold">
                {profileData.full_name.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-1">{profileData.full_name || 'Kullanıcı'}</h2>
                <p className="text-white/60">{profileData.email}</p>
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSave} className="p-8 space-y-6">
            {/* Success/Error Message */}
            {message && (
              <div className={`p-4 rounded-lg ${message.includes('✅') ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
                {message}
              </div>
            )}

            {/* Full Name */}
            <div>
              <label className="block text-white/80 mb-2 font-medium">
                <i className="ri-user-line mr-2"></i>
                Ad Soyad
              </label>
              <input
                type="text"
                value={profileData.full_name}
                onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-500/50 transition-colors"
                placeholder="Adınız ve soyadınız"
                required
              />
            </div>

            {/* Email (Read-only) */}
            <div>
              <label className="block text-white/80 mb-2 font-medium">
                <i className="ri-mail-line mr-2"></i>
                E-posta
              </label>
              <input
                type="email"
                value={profileData.email}
                disabled
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white/60 cursor-not-allowed"
              />
              <p className="text-white/40 text-sm mt-1">E-posta adresi değiştirilemez</p>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-white/80 mb-2 font-medium">
                <i className="ri-phone-line mr-2"></i>
                Telefon
              </label>
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-orange-500/50 transition-colors"
                placeholder="+90 5XX XXX XX XX"
              />
            </div>

            {/* Account Info */}
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h3 className="text-white font-semibold mb-4 flex items-center">
                <i className="ri-information-line mr-2 text-blue-400"></i>
                Hesap Bilgileri
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Kullanıcı ID:</span>
                  <span className="text-white/80 font-mono">{user?.id.slice(0, 8)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Hesap Durumu:</span>
                  <span className="text-green-400 flex items-center">
                    <i className="ri-checkbox-circle-fill mr-1"></i>
                    Aktif
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Kayıt Tarihi:</span>
                  <span className="text-white/80">
                    {new Date(user?.created_at || '').toLocaleDateString('tr-TR')}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-4">
              <Link
                to="/dashboard"
                className="px-6 py-3 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Geri Dön
              </Link>
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line mr-2"></i>
                    Değişiklikleri Kaydet
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Danger Zone */}
        <div className="mt-8 bg-red-500/10 backdrop-blur-sm rounded-2xl border border-red-500/30 p-8">
          <h3 className="text-xl font-bold text-red-400 mb-2 flex items-center">
            <i className="ri-alert-line mr-2"></i>
            Tehlikeli Bölge
          </h3>
          <p className="text-white/60 mb-4">Bu işlemler geri alınamaz. Dikkatli olun.</p>
          <button
            onClick={handleLogout}
            className="px-6 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-circle-line mr-2"></i>
            Oturumu Kapat
          </button>
        </div>
      </div>
    </div>
  );
}
